/**
 * Sign in, and join.
 *
 * One screen for both, because the difference between "I have an account" and
 * "I do not yet" is one the member should not have to navigate to resolve —
 * a volunteer reading out a URL at the hall says "sign in", not "sign in, or
 * tap register if this is your first time".
 *
 * Registering here creates an ACCOUNT, not a Sangam membership. A guest can
 * book a ticket without joining the association; membership is a paid order
 * and a later build step.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ErrorNote, Shell } from "../components/Shell";
import type { Locale } from "../i18n";
import { ApiError, api, storeTokens } from "../lib/api";

export default function SignIn({ locale }: { locale: Locale }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Where the member was headed before they were asked to sign in.
  const next = params.get("next") || "/";

  const [joining, setJoining] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const tokens = joining
        ? await api.register({ name: name.trim(), email: email.trim(), password, locale })
        : await api.login({ email: email.trim(), password });
      storeTokens(tokens);
      // Replace, so Back does not return to a sign-in form for a session that
      // is now live.
      navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // 401 is a wrong password, 409 an address already in use, 429 a
        // lockout. Each gets its own sentence rather than one shrug.
        if (err.status === 401) setError(t("auth.wrong"));
        else if (err.status === 409) setError(t("auth.taken"));
        else if (err.status === 429) setError(t("auth.locked"));
        else if (err.status === 422) setError(t("auth.invalid"));
        else setError(t("error.generic"));
      } else {
        setError(t("error.network"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell masthead area="home" title={joining ? t("auth.join") : t("auth.sign_in")}>
      <form className="stack" onSubmit={submit}>
        {joining && (
          <label className="labelled">
            {t("checkout.name")}
            <input
              className="field"
              value={name}
              autoComplete="name"
              required
              onChange={(e) => setName(e.target.value)}
            />
          </label>
        )}

        <label className="labelled">
          {t("checkout.email")}
          <input
            className="field"
            type="email"
            value={email}
            autoComplete="email"
            inputMode="email"
            required
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="labelled">
          {t("auth.password")}
          <input
            className="field"
            type="password"
            value={password}
            /* Tells a password manager which of the two this is, so it offers
               to save on joining and to fill on returning. */
            autoComplete={joining ? "new-password" : "current-password"}
            minLength={joining ? 8 : undefined}
            required
            onChange={(e) => setPassword(e.target.value)}
          />
          {joining && <span className="fallback-note">{t("auth.password_hint")}</span>}
        </label>

        {error && <ErrorNote>{error}</ErrorNote>}

        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? t("common.loading") : joining ? t("auth.join") : t("auth.sign_in")}
        </button>
      </form>

      <button
        type="button"
        className="btn"
        onClick={() => {
          setJoining(!joining);
          setError(null);
        }}
      >
        {joining ? t("auth.have_account") : t("auth.no_account")}
      </button>
    </Shell>
  );
}
